import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { connect } from 'redux-bundler-react';
import { withStyles, useStyles } from 'tss-react/mui';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Editor from '@monaco-editor/react';
import React from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import ArchChefPackageSelect from '../../organisms/ArchChefPackageSelect';
import ArchDetailRow from '../../atoms/ArchDetailRow';
import ArchRecipeChipInput from '../../organisms/ArchRecipeChipInput';
import ChefPackage from '../../../models/chef_package';
import ChefPackagePresignedUrl from '../../../models/chef_package_presigned_url';
import PusherSubscription from '../../atoms/PusherContainer/PusherSubscription';

const styles = theme => ({
  withEditor: {
    '&&': {
      overflow: 'hidden',
    },
  },
  root: {
    width: 650,
    margin: theme.spacing(2),
    marginRight: 0,
    position: 'relative',
    flexGrow: 0,
    flexShrink: 0,
    overflowY: 'auto',
  },
  header: {
    marginTop: 0,
    marginBottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
  },
  menu: {
    zIndex: 9999,
  },

  justify: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  runList: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  warning: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  },
  chefPackageDetails: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
  },

  emptyText: {
    width: '100%',
    textAlign: 'center',
    padding: theme.spacing(2),
  },

  recipeEditor: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    zIndex: 9999,
    top: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    margin: 0,
  },

  recipeEditorTextField: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
  },
  recipeEditorActions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
  },
});

const Scripts = ({
  classes,
  onCommand,
  activeFacility,
  activeClient,
  xtermRef,
  workstation,
  platform,
}) => {
  const effectivePlatform =
    platform || workstation?.image?.platform || workstation?.platform;
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [runList, setRunList] = React.useState([]);
  const [cookbook, setCookbook] = React.useState(null);
  const [presignedUrl, setPresignedUrl] = React.useState(null);
  const [chefPackage, setChefPackage] = React.useState(null);
  const [chefPackageUpdating, setChefPackageUpdating] = React.useState(false);
  const [chefPackageUpdated, setChefPackageUpdated] = React.useState(false);
  const [commandId, setCommandId] = React.useState(null);
  const [commandStartedAt, setCommandStartedAt] = React.useState(null);
  const [intervalId, setIntervalId] = React.useState(null);
  const [duration, setDuration] = React.useState(null);

  const [editingRecipe, setEditingRecipe] = React.useState(null);
  const [recipeContent, setRecipeContent] = React.useState(null);
  const [
    chefPackageWithCookbooks,
    setChefPackageWithCookbooks,
  ] = React.useState(null);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  React.useEffect(() => {
    if (!chefPackage) return undefined;
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await ChefPackage.includes([
          { last_chef_package_sync: 'performed_by' },
          'last_analysis',
          'githubAppInstallation',
          { cookbooks: 'recipes' },
        ]).find(chefPackage.id);

        if (isMounted) {
          console.log(response.data);
          setChefPackageWithCookbooks(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [chefPackage]);

  const handleSaveRecipe = () => {
    const cookbook = editingRecipe.split('::')[0];
    const recipeName = editingRecipe.split('::')[1];

    if (effectivePlatform === 'windows') {
      onCommand(
        `$content = '${btoa(
          recipeContent,
        )}'; $bytes = [System.Convert]::FromBase64String($content); Set-Content -Path "c:\\cookbooks\\${cookbook}\\recipes\\${recipeName}.rb" -Value $bytes -Encoding Byte
`,
        true,
      );
    }

    if (effectivePlatform === 'linux') {
      onCommand(
        `echo '${btoa(
          recipeContent,
        )}' | base64 --decode > "/tmp/cookbooks/${cookbook}/recipes/${recipeName}.rb"`,
        true,
      );
    }
  };
  const handleEditRecipe = async recipe => {
    const cookbook = recipe.split('::')[0];
    const recipeName = recipe.split('::')[1];

    if (effectivePlatform === 'windows') {
      onCommand(
        `[convert]::ToBase64String((Get-Content -path "c:\\cookbooks\\${cookbook}\\recipes\\${recipeName}.rb" -Encoding byte)) | %{ ">>>$_<<<" }`,
        true,
      );
    }

    if (effectivePlatform === 'linux') {
      onCommand(
        `base64_contents=$(base64 "/tmp/cookbooks/${cookbook}/recipes/${recipeName}.rb"); echo ">>>$base64_contents<<<"`,
        true,
      );
    }

    //wait 10 seconds
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log(xtermRef.current.terminal.buffer.active.length);
    let text = '';
    for (let i = 0; i < xtermRef.current.terminal.buffer.active.length; i++) {
      text =
        text +
        xtermRef.current.terminal.buffer.active
          .getLine(i)
          .translateToString(true);
    }
    let lastBase64 = text.match(/>>>(.*?)<<</g).pop();
    // Clean up the delimiter and decode
    lastBase64 = lastBase64.replace(/>>>/, '').replace(/<<</, '');
    let decodedText = atob(lastBase64);

    console.log(decodedText);
    setEditingRecipe(recipe);
    setRecipeContent(decodedText);
  };

  React.useEffect(() => {
    if (commandId) {
      const interval = setInterval(() => {
        let text = '';
        for (
          let i = 0;
          i < xtermRef.current.terminal.buffer.active.length;
          i++
        ) {
          text =
            text +
            xtermRef.current.terminal.buffer.active
              .getLine(i)
              .translateToString(true);
        }

        const now = Math.round(new Date().getTime() / 1000);
        const startTimeAsEpoch = Math.round(
          new Date(commandStartedAt).getTime() / 1000,
        );
        const nextDuration = now - startTimeAsEpoch;
        setDuration(nextDuration);

        if (text.includes(`Completed ${commandId}`)) {
          setCommandId(null);
        }
      }, 1000);

      console.log('setting interval ', interval);
      setIntervalId(interval);
    } else {
      if (intervalId) {
        console.log('Clearing interval', intervalId);
        clearInterval(intervalId);
        setIntervalId(null);
      }
    }
  }, [commandId]);

  const { cx } = useStyles();

  const executeRunList = () => {
    const commandId = crypto.randomUUID();
    setCommandId(commandId);
    setCommandStartedAt(new Date());
    setDuration(null);

    let encodedCommandId = btoa(commandId);
    console.log(effectivePlatform);

    if (effectivePlatform === 'windows') {
      onCommand(
        `$commandId = [System.Text.Encoding]::UTF8.GetString([System.Convert]::FromBase64String("${encodedCommandId}")); echo "Starting $commandId"; c:; cd \\; cinc-solo -c solo.rb  -j config.json -o ${runList.join(
          ',',
        )}; echo "Completed $commandId";`,
        true,
      );
    }

    if (effectivePlatform === 'linux') {
      onCommand(
        `commandId=$(echo "${encodedCommandId}" | base64 --decode); echo "Starting $commandId"; cd /tmp; sudo cinc-solo -c solo.rb  -j config.json -o ${runList.join(
          ',',
        )}; echo "Completed $commandId";`,
        true,
      );
    }
  };

  const installChefPackage = async () => {
    setChefPackageUpdated(false);
    const presignedUrl = new ChefPackagePresignedUrl();
    presignedUrl.chefPackage = chefPackage;
    presignedUrl.readOnly = true;
    await presignedUrl.save({ with: 'chefPackage.id' });
    setPresignedUrl(presignedUrl);

    if (effectivePlatform === 'linux') {
      onCommand(
        `cd /tmp/; wget -nv -O /tmp/cookbooks.tgz "${
          presignedUrl.downloadUrl
        }"; rm -rf /tmp/cookbooks; tar -zxf /tmp/cookbooks.tgz; echo 'cookbook_path ["/tmp/cookbooks"]' > solo.rb; echo '{"external_id": "${
          activeClient.id
        }", "facility_id": "${
          activeFacility.id
        }", "short_facility_id": "${activeFacility.shortId()}", "region": "us-west-2", "role": "arn:aws:iam::520148785661:role/scriptDevelopmentRole"}' > config.json`,

        true,
      );
    }

    if (effectivePlatform === 'windows') {
      onCommand(
        `cd c:\\; Invoke-WebRequest -Uri "${
          presignedUrl.downloadUrl
        }" -OutFile c:\\cookbooks.tgz; tar -xzf c:\\cookbooks.tgz -C c:\\; Set-Content -Path 'C:\\solo.rb' -Value 'cookbook_path ["C:\\\\cookbooks"]'; Set-Content -Path 'C:\\config.json' -value '{"external_id": "${
          activeClient.id
        }", "facility_id": "${
          activeFacility.id
        }", "short_facility_id": "${activeFacility.shortId()}", "region": "us-west-2", "role": "arn:aws:iam::520148785661:role/scriptDevelopmentRole"}'`,
        true,
      );
    }
  };

  return (
    <div className={cx(classes.root, editingRecipe && classes.withEditor)}>
      {chefPackageWithCookbooks && (
        <PusherSubscription
          channelName={`private-chef-package-${chefPackageWithCookbooks.id}`}
          eventName="update"
          onUpdate={async () => {
            const response = await ChefPackage.includes([
              { last_chef_package_sync: 'performed_by' },
              'last_analysis',
              'githubAppInstallation',
              { cookbooks: 'recipes' },
            ]).find(chefPackage.id);

            const nextChefPackageWithCookbooks = response.data;
            const nextChefPackageSync =
              nextChefPackageWithCookbooks.lastChefPackageSync;
            const { lastChefPackageSync } = chefPackageWithCookbooks;

            if (nextChefPackageSync?.id !== lastChefPackageSync?.id) {
              if (nextChefPackageSync?.isComplete()) {
                setChefPackageWithCookbooks(nextChefPackageWithCookbooks);
                setChefPackageUpdating(false);
                setChefPackageUpdated(true);
              } else {
                setChefPackageUpdating(true);
                setChefPackageUpdated(false);
              }
            }
          }}
        />
      )}
      {editingRecipe && (
        <Paper className={classes.recipeEditor}>
          Editing {editingRecipe}
          {/* <textarea */}
          {/*   multiline */}
          {/*   className={classes.recipeEditorTextField} */}
          {/*   onChange={e => setRecipeContent(e.target.value)} */}
          {/* > */}
          {/*   {recipeContent} */}
          {/* </textarea> */}
          <Editor
            wrapperProps={{ className: classes.recipeEditorTextField }}
            defaultValue={recipeContent}
            defaultLanguage="ruby"
            options={{
              minimap: { enabled: false },
              wordWrap: 'off',
            }}
            theme="vs-dark"
            onChange={code => setRecipeContent(code)}
            padding={10}
          />
          <div className={classes.recipeEditorActions}>
            <Button onClick={() => setEditingRecipe(null)}>Cancel</Button>
            <Button
              onClick={() => {
                handleSaveRecipe();
                setEditingRecipe(null);
              }}
            >
              Save
            </Button>
          </div>
        </Paper>
      )}
      <h3 className={classes.header}>Script Package</h3>
      <ArchChefPackageSelect
        value={chefPackage}
        onChange={e => {
          setChefPackage(e.target.value);
          setChefPackageWithCookbooks(null);
        }}
      />
      {chefPackageWithCookbooks === null && chefPackage && (
        <Paper
          variant="outlined"
          className={cx(classes.chefPackageDetails, classes.loading)}
        >
          <CircularProgress />
        </Paper>
      )}
      {chefPackageWithCookbooks && (
        <>
          {chefPackageUpdating && (
            <Alert severity="info">Script Package is building...</Alert>
          )}
          {chefPackageUpdated && (
            <Alert severity="success">
              Updated Script Package ready for install
            </Alert>
          )}
          <Paper variant="outlined" className={classes.chefPackageDetails}>
            <ArchDetailRow
              label="Script Package Name"
              value={chefPackageWithCookbooks.name}
            />

            {chefPackageWithCookbooks.isGithub() && (
              <ArchDetailRow
                label="Last Commit"
                value={
                  chefPackageWithCookbooks.lastChefPackageSync.commitMessage
                }
              />
            )}

            {chefPackageWithCookbooks.isUpload() && (
              <ArchDetailRow
                label="Last Upload"
                value={
                  chefPackageWithCookbooks.lastChefPackageSync.commitMessage
                }
              />
            )}

            <Button variant="contained" onClick={installChefPackage}>
              Install
            </Button>
          </Paper>
        </>
      )}
      <br />
      <h3 className={classes.header}>
        Run List
        <IconButton
          className={classes.copy}
          size="small"
          onClick={event => setMenuAnchor(event.currentTarget)}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          className={classes.menu}
        >
          <MenuItem
            disabled={chefPackageWithCookbooks === null}
            onClick={async () => {
              const result = await window.navigator.clipboard.readText();
              const nextRunList = result
                .split('\n')
                .filter(Boolean)
                .map(r => r.replace(/(\r\n|\n|\r)/gm, ''));

              setRunList(nextRunList);
              setMenuAnchor(null);
            }}
          >
            Import from clipboard
          </MenuItem>

          <MenuItem
            disabled={chefPackageWithCookbooks === null}
            onClick={async () => {
              const textToCopy = runList.join('\n');
              window.navigator.clipboard.writeText(textToCopy);

              setMenuAnchor(null);
            }}
          >
            Export to clipboard
          </MenuItem>
        </Menu>
      </h3>
      {chefPackageWithCookbooks && (
        <ArchRecipeChipInput
          disablePortal
          chefPackage={chefPackageWithCookbooks}
          fullWidth
          value={runList}
          onChange={recipe => setRunList([...runList, recipe])}
        />
      )}
      {commandId && duration && (
        <>
          <br />
          <Alert
            onClose={() => {
              setCommandId(null);
              setDuration(null);
            }}
            severity="warning"
          >
            Execution in progress {duration}s
          </Alert>
        </>
      )}

      {!commandId && duration && (
        <>
          <br />
          <Alert severity="info">Execution finished {duration}s</Alert>
        </>
      )}
      {runList.length > 0 ? (
        <>
          <Paper variant="outlined" className={classes.runList}>
            <List>
              {runList.map((item, index) => (
                <ListItem key={index}>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                    {item}
                  </ListItemText>

                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      size="small"
                      onClick={() => handleEditRecipe(item)}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      size="small"
                      onClick={() =>
                        setRunList(runList.filter(i => i !== item))
                      }
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
          <div className={classes.justify}>
            <Button variant="contained" onClick={() => setRunList([])}>
              Clear
            </Button>
            <Button
              variant="contained"
              disabled={Boolean(commandId)}
              onClick={executeRunList}
            >
              Execute
            </Button>
          </div>
        </>
      ) : (
        <Paper variant="outlined" className={classes.runList}>
          <div className={classes.emptyText}>
            <Typography variant="body2" color="textSecondary">
              No Items
            </Typography>
          </div>
        </Paper>
      )}
      <Paper variant="outlined" className={classes.warning}>
        Note: Permissions to access the Arch S3 Bucket must be configured
        seperately. See the Arch documentation for more information.
      </Paper>
    </div>
  );
};

export default connect(
  'selectActiveFacility',
  'selectActiveClient',
  'doUpdateUrl',
  withStyles(Scripts, styles),
);
